import {
  FormPackagesDrawer,
  useToast,
  type FormPackage,
  type FormPackageDraft,
  type FormPackageFormOption,
} from '@elva/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { keys } from '@/api/queryKeys';
import { extractApiErrorMessage } from '@/api/errors';
import { adaptFormPackage } from '../adapters/formsAdapters';
import { formsApi } from '../api/formsApi';
import { useCreateFormPackage, useDeleteFormPackage, useFormPackages } from '../hooks/useFormPackages';

const EMPTY_DRAFT: FormPackageDraft = {
  name: '',
  description: '',
  trigger: '',
  formIds: [],
};

interface ManagePackagesDrawerProps {
  open: boolean;
  onClose: () => void;
  formOptions: FormPackageFormOption[];
}

export function ManagePackagesDrawer({ open, onClose, formOptions }: ManagePackagesDrawerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading } = useFormPackages();
  const createPackage = useCreateFormPackage();
  const deletePackage = useDeleteFormPackage();

  const packages = useMemo(
    () => (data?.packages ?? []).map(adaptFormPackage),
    [data?.packages],
  );

  const [view, setView] = useState<'list' | 'edit'>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<FormPackageDraft>(EMPTY_DRAFT);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setView('list');
      setEditingId(null);
      setDraft(EMPTY_DRAFT);
      return;
    }
    void queryClient.invalidateQueries({ queryKey: keys.forms.packages });
    void queryClient.invalidateQueries({ queryKey: keys.formsStats });
  }, [open, queryClient]);

  const loadPackageIntoDraft = (pkg: FormPackage) => {
    setEditingId(pkg.id);
    setDraft({
      name: pkg.name,
      description: pkg.description ?? '',
      trigger: pkg.trigger ?? '',
      formIds: [...pkg.forms],
    });
    setView('edit');
  };

  const handleSave = async () => {
    const trimmedName = draft.name.trim();
    if (!trimmedName) {
      toast({ title: 'Name is required', variant: 'error' });
      return;
    }

    const payload = {
      name: trimmedName,
      forms: draft.formIds,
      ...(draft.description.trim() ? { description: draft.description.trim() } : { description: '' }),
      ...(draft.trigger.trim() ? { trigger: draft.trigger.trim() } : { trigger: '' }),
    };

    try {
      setIsSaving(true);
      if (editingId) {
        await formsApi.patchPackage(editingId, payload);
        await queryClient.invalidateQueries({ queryKey: keys.forms.packages });
        await queryClient.invalidateQueries({ queryKey: keys.formsStats });
        toast({ title: 'Package updated', variant: 'success' });
      } else {
        await createPackage.mutateAsync(payload);
        await queryClient.invalidateQueries({ queryKey: keys.forms.packages });
        await queryClient.invalidateQueries({ queryKey: keys.formsStats });
        toast({ title: 'Package created', variant: 'success' });
      }
      setView('list');
      setEditingId(null);
      setDraft(EMPTY_DRAFT);
    } catch (error) {
      toast({ title: 'Save failed', description: extractApiErrorMessage(error), variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      await deletePackage.mutateAsync(editingId);
      toast({ title: 'Package deleted', variant: 'success' });
      setView('list');
      setEditingId(null);
      setDraft(EMPTY_DRAFT);
    } catch (error) {
      toast({ title: 'Delete failed', description: extractApiErrorMessage(error), variant: 'error' });
    }
  };

  const isBusy = isSaving || createPackage.isPending || deletePackage.isPending;

  return (
    <FormPackagesDrawer
      open={open}
      onClose={onClose}
      packages={packages}
      formOptions={formOptions}
      view={view}
      editingId={editingId}
      draft={draft}
      onDraftChange={setDraft}
      onSelectPackage={(id) => {
        const pkg = packages.find((item) => item.id === id);
        if (pkg) loadPackageIntoDraft(pkg);
      }}
      onCreateNew={() => {
        setEditingId(null);
        setDraft(EMPTY_DRAFT);
        setView('edit');
      }}
      onBackToList={() => {
        setView('list');
        setEditingId(null);
        setDraft(EMPTY_DRAFT);
      }}
      onSave={() => {
        void handleSave();
      }}
      {...(editingId
        ? {
            onDelete: () => {
              void handleDelete();
            },
          }
        : {})}
      isLoading={isLoading}
      isSaving={isBusy}
    />
  );
}
